import { BLUEPRINT_DEFAULT_SCALE, DEFAULT_PRINT_MARGINS, DOCUMENT_SIZES, DocumentSize } from '@editor/config/constants'
import { PointArray } from '@editor/interfaces/attribute'

/**
 * Calculate the default print scale to fit a bounding box into a document size.
 *
 * @param boundingBox - The bounding box as [min, max] point arrays (can be 3D or 2D projected)
 * @param documentSize - The target document size (A4, A3, A2, A1)
 * @param is2DProjected - Whether the bounding box is already 2D projected (default: false)
 * @returns Print scale ratio (e.g., 0.01 for 1:100 scale)
 */
export const autoCalculatePrintScale = (
  boundingBox: [PointArray, PointArray],
  documentSize: DocumentSize = 'A4',
): number => {
  if (!boundingBox || boundingBox.length !== 2) {
    return BLUEPRINT_DEFAULT_SCALE // Default scale if no bounding box
  }

  const [min, max] = boundingBox
  if (!min || !max || min.length < 2 || max.length < 2) {
    return BLUEPRINT_DEFAULT_SCALE // Default scale if invalid bounding box
  }

  // Calculate 2D dimensions of the point cloud (X and Y axes)
  const width = Math.abs(max[0] - min[0]) * 1000 // X dimension in real world units (mm)
  const height = Math.abs(max[1] - min[1]) * 1000 // Y dimension in real world units (mm)

  // Get document dimensions and subtract margins
  const doc = DOCUMENT_SIZES[documentSize]
  const printableWidth = doc.width - DEFAULT_PRINT_MARGINS * 2
  const printableHeight = doc.height - DEFAULT_PRINT_MARGINS * 2

  // Calculate scale factors for both dimensions
  const scaleX = printableWidth / width
  const scaleY = printableHeight / height

  // Use the smaller scale factor to ensure the entire model fits
  const scale = Math.min(scaleX, scaleY)

  // Ensure the scale is positive and reasonable (between 1:10000 and 10:1)
  return Math.max(0.0001, Math.min(10, scale))
}

/**
 * Convert a print scale ratio to a human-readable scale string.
 *
 * @param scale - Print scale ratio (e.g., 0.01)
 * @returns Scale string (e.g., "100" for 1:100 scale)
 */
export const scaleToString = (scale: number): string => {
  if (!scale || scale <= 0) return '100'

  const ratio = 1 / scale
  return Math.round(ratio).toString()
}

/**
 * Convert a scale string to a print scale ratio.
 *
 * @param scaleString - Scale string (e.g., "100" for 1:100 scale)
 * @returns Print scale ratio (e.g., 0.01)
 */
export const stringToScale = (scaleString: string): number => {
  const ratio = parseFloat(scaleString)
  if (Number.isNaN(ratio) || ratio <= 0) return BLUEPRINT_DEFAULT_SCALE

  return 1 / ratio
}

/**
 * Validate a print scale value.
 *
 * @param scale - Print scale ratio to validate
 * @returns True if the scale is valid
 */
export const isValidScale = (scale: number): boolean =>
  typeof scale === 'number' && scale > 0 && scale <= 10 && scale >= 0.0001
